import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  computed,
  inject,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MessageService } from 'primeng/api';
import { utils } from '@app/shared';
import { TicketService } from '../../../core/services/ticket.service';
import { TicketDetail, TicketLog, TicketTask } from '../../../core/models/ticket.model';
import { constants } from '../../../../../shared/utils/constants';
import { firstValueFrom } from 'rxjs';
import {
  EntityPickerComponent,
  EntityPickerSearchParams,
  EntityPickerResult,
} from '../../entity-picker';
import { FavoriteSelectComponent } from '../../shared/favorite-input';
import { FavoriteListKey } from '../../../core/models/favorite-list.model';
import { FavoriteOptionsService } from '../../../core/services/favorite-options.service';
import { TimeEntryHistoryComponent } from '../time-entry-history/time-entry-history.component';
import { StorageManagerService } from '@app/shared/services/storage-manager.service';

export type ActionType = 'comment' | 'attachment' | 'timeentry';

/** Registrada em `storage-key-registry.ts` (HIGH/90d); sem registro seria tratada como LOW/30d. */
const COMMENT_PUB_KEY = 'ticket_log_comment_pub';

@Component({
  selector: 'app-time-entry-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    CheckboxModule,
    SelectModule,
    DatePickerModule,
    TooltipModule,
    ToggleSwitchModule,
    EntityPickerComponent,
    FavoriteSelectComponent,
    TimeEntryHistoryComponent,
  ],
  templateUrl: './time-entry-modal.component.html',
  styleUrls: ['./time-entry-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class TimeEntryModalComponent {
  /** Enum das listas de favoritos, para uso no template. */
  protected readonly FavoriteKey = FavoriteListKey;

  private readonly messageService = inject(MessageService);
  private readonly ticketService = inject(TicketService);
  private readonly favoriteOptions = inject(FavoriteOptionsService);
  private readonly storage = inject(StorageManagerService);

  @Input({ required: true }) set ticket(value: TicketDetail) {
    this._ticket = value;
    this.ticketTasks.set(value.tasks || []);
  }
  get ticket(): TicketDetail {
    return this._ticket;
  }
  private _ticket!: TicketDetail;
  private ticketTasks = signal<TicketTask[]>([]);

  @Input() ticketTimeEntries: TicketLog[] = [];

  @Input() set activeTimeEntry(value: TicketLog | null) {
    this._activeTimeEntry = value;
    if (this._visible) {
      this.updateActiveTab();
    }
  }

  get activeTimeEntry(): TicketLog | null {
    return this._activeTimeEntry;
  }

  private _activeTimeEntry: TicketLog | null = null;
  activeTab = signal<'new' | 'history'>('new');

  // Status search state
  statusesList = signal<any[]>([]);
  statusesLoading = signal(false);

  timeEntrySystem = signal<any | null>(null);

  readonly searchSystemsFn = (params: EntityPickerSearchParams): Promise<EntityPickerResult> =>
    firstValueFrom(this.ticketService.searchSystems(params.query || '')).then(items => ({
      items,
      total: items.length,
    }));

  private _initialTaskId: string | null = null;
  private _isInitializing = false;

  @Input() set initialTaskId(value: string | null) {
    this._initialTaskId = value;
  }

  get initialTaskId(): string | null {
    return this._initialTaskId;
  }

  @Input() set visible(value: boolean) {
    this._visible = value;
    if (value) {
      this._isInitializing = true;
      this.resetForm();

      if (!this._activeTimeEntry) {
        this.detectActiveTimeEntry();
      }

      if (this.ticket && this.ticket.status) {
        this.statusesList.set([this.ticket.status]);
      }

      this.updateActiveTab();

      setTimeout(() => {
        if (this._activeTimeEntry) {
          // Formulário já foi populado em populateFormFromActiveEntry()
        } else if (this._initialTaskId) {
          const taskIdToUse = this._initialTaskId;
          this.timeEntryTaskId.set(taskIdToUse);
          this.resolveSystem(taskIdToUse);
          this.detectAndSetFollowUpType();
        } else {
          this.preSelectTaskFromOtherUserEntry();
        }

        this._isInitializing = false;
      }, 0);

      this.loadInitialStatuses();
    }
  }

  get visible(): boolean {
    return this._visible;
  }

  private _visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() timeEntryAdded = new EventEmitter<TicketDetail>();
  @Output() timeEntryStopped = new EventEmitter<void>();

  // TimeEntry properties
  timeEntryType = signal('M'); // M = Manutenção
  timeEntryAttendanceType = signal('I'); // I = Interno
  timeEntryDepartmentType = signal('J'); // J = Desenvolvimento Java
  timeEntryStartDate = signal(new Date());
  timeEntryEndDate = signal<Date | null>(null);
  timeEntryTaskId = signal<string | null>(null);
  timeEntryStatusId = signal<string | null>(null);
  /** Concluir a tarefa apontada ao encerrar. Desligado por padrão — ver canFinishTask(). */
  finishTask = signal(false);
  sending = signal(false);

  /** Comentário do apontamento — destino descrito em TicketService.startTimeEntry. */
  movementDescription = signal('');

  /**
   * Visibilidade do comentário gerado. Público é o padrão: o histórico do apontamento é justamente
   * o que o cliente acompanha no HelpDesk.
   */
  commentPub = signal<boolean>(this.carregarCommentPub());

  onCommentPubChange(value: boolean): void {
    this.commentPub.set(value);
    try {
      this.storage.setItem(COMMENT_PUB_KEY, value ? '1' : '0');
    } catch {
      /* preferência de visibilidade: falhar em salvar não pode impedir o apontamento */
    }
  }

  private carregarCommentPub(): boolean {
    try {
      // `'0'` explícito, não falsy: valor ausente, expirado ou legacy cai no default de público.
      return this.storage.getItem(COMMENT_PUB_KEY) !== '0';
    } catch {
      return true;
    }
  }

  private updateActiveTab() {
    if (!this._activeTimeEntry) {
      this.detectActiveTimeEntry();
    }

    if (this._activeTimeEntry) {
      this.activeTab.set('new');
      this.populateFormFromActiveEntry();
    } else {
      const user = utils.getUser();
      const bwUserCG = utils.getBwUserCompanyGroup(user);
      const correctUserId = bwUserCG?.user_id || user?.id;

      const hasUserActiveEntry = this.ticketTimeEntries?.some(
        entry =>
          !entry.end_date && (entry.user_id === correctUserId || entry.user?.id === correctUserId)
      );

      if (!hasUserActiveEntry) {
        this.activeTab.set('new');
      }
    }
  }

  private detectActiveTimeEntry(): void {
    if (this.ticketTimeEntries && this.ticketTimeEntries.length > 0) {
      const user = utils.getUser();
      const bwUserCG = utils.getBwUserCompanyGroup(user);
      const correctUserId = bwUserCG?.user_id || user?.id;

      const activeEntry = this.ticketTimeEntries.find(
        entry =>
          !entry.end_date && (entry.user_id === correctUserId || entry.user?.id === correctUserId)
      );
      if (activeEntry) {
        this._activeTimeEntry = activeEntry;
      }
    }
  }

  private detectAndSetFollowUpType(): void {
    const selectedTaskId = this.timeEntryTaskId();
    const user = utils.getUser();
    const bwUserCG = utils.getBwUserCompanyGroup(user);
    const correctUserId = bwUserCG?.user_id || user?.id;

    const activeEntryByOtherUser = this.ticketTimeEntries?.find(entry => {
      if (entry.end_date) return false;

      const entryUserId = entry.user_id || entry.user?.id;
      if (entryUserId === correctUserId) return false;

      const entryTaskId = entry.task_id?.toString() || null;
      const normalizedSelectedTaskId = selectedTaskId?.toString() || null;

      return entryTaskId === normalizedSelectedTaskId;
    });

    if (activeEntryByOtherUser) {
      this.timeEntryType.set('A');
      return;
    }

    if (selectedTaskId) {
      const task = this.ticketTasks().find(t => t.id.toString() === selectedTaskId.toString());
      if (task?.user && task.user.id !== correctUserId) {
        this.timeEntryType.set('A');
      }
    }
  }

  private detectAndSetFollowUpTypeForTicket(): void {
    const user = utils.getUser();
    const bwUserCG = utils.getBwUserCompanyGroup(user);
    const correctUserId = bwUserCG?.user_id || user?.id;

    const activeEntryByOtherUser = this.ticketTimeEntries?.find(entry => {
      if (entry.end_date) return false;

      const entryUserId = entry.user_id || entry.user?.id;
      if (entryUserId === correctUserId) return false;

      return !entry.task_id;
    });

    if (activeEntryByOtherUser) {
      this.timeEntryType.set('A');
      return;
    }

    const developer = this.ticket?.developer;
    if (developer && developer.id !== correctUserId) {
      this.timeEntryType.set('A');
    }
  }

  private preSelectTaskFromOtherUserEntry(): void {
    const user = utils.getUser();
    const bwUserCG = utils.getBwUserCompanyGroup(user);
    const correctUserId = bwUserCG?.user_id || user?.id;

    const userActiveEntry = this.ticketTimeEntries?.find(entry => {
      if (entry.end_date) return false;

      const entryUserId = entry.user_id || entry.user?.id;
      return entryUserId === correctUserId;
    });

    if (userActiveEntry && userActiveEntry.task_id) {
      this.timeEntryTaskId.set(userActiveEntry.task_id.toString());
      this.detectAndSetFollowUpType();
    } else {
      this.detectAndSetFollowUpTypeForTicket();
    }
  }

  private populateFormFromActiveEntry(): void {
    if (!this._activeTimeEntry) return;

    const now = new Date();

    this.timeEntryType.set(this._activeTimeEntry.type || 'M');
    this.timeEntryAttendanceType.set(this._activeTimeEntry.attendance_type || 'I');
    this.timeEntryDepartmentType.set(this._activeTimeEntry.department_type || 'J');
    this.timeEntryStartDate.set(
      this._activeTimeEntry.start_date ? new Date(this._activeTimeEntry.start_date) : new Date()
    );
    this.timeEntryEndDate.set(
      this._activeTimeEntry.end_date ? new Date(this._activeTimeEntry.end_date) : now
    );
    this.timeEntryTaskId.set(this._activeTimeEntry.task_id?.toString() || null);
    this.timeEntryStatusId.set(
      this._activeTimeEntry.ticket_status_id || this.ticket?.status?.id || null
    );
    this.timeEntrySystem.set(this._activeTimeEntry.system ?? this._ticket?.system ?? null);
  }

  /**
   * Busca inicial dos status ao abrir o modal. É pulada quando a seleção vai
   * abrir só com os favoritos — o status default não depende dessa resposta.
   */
  private loadInitialStatuses(): void {
    this.applyDefaultStatus();

    if (this.favoriteOptions.hasFavorites(FavoriteListKey.TicketStatuses)) return;
    this.loadStatuses();
  }

  private applyDefaultStatus(): void {
    if (!this._activeTimeEntry && !this.timeEntryStatusId()) {
      this.timeEntryStatusId.set(this.ticket?.status?.id || null);
    }
  }

  private loadStatuses(query: string = ''): void {
    this.statusesLoading.set(true);
    this.ticketService.searchTicketStatuses(query).subscribe({
      next: statuses => {
        const currentId = this.timeEntryStatusId();
        if (currentId && !statuses.some((s: any) => s.id === currentId)) {
          if (this.ticket?.status && this.ticket.status.id === currentId) {
            statuses.unshift(this.ticket.status);
          }
        }
        this.statusesList.set(statuses);
        this.applyDefaultStatus();
        this.statusesLoading.set(false);
      },
      error: () => {
        this.statusesLoading.set(false);
      },
    });
  }

  onStatusFilter(event: any): void {
    this.loadStatuses(event.filter || '');
  }

  private resolveSystem(taskId: string | null): void {
    if (taskId) {
      const task = this.ticketTasks().find(t => t.id.toString() === taskId.toString());
      this.timeEntrySystem.set(task?.systems?.[0] ?? this._ticket?.system ?? null);
    } else {
      this.timeEntrySystem.set(this._ticket?.system ?? null);
    }
  }

  onTaskSelectionChange(selectedTaskId: string | null): void {
    if (this._isInitializing) {
      return;
    }

    if (this._activeTimeEntry) {
      const activeTaskId = this._activeTimeEntry.task_id?.toString() || null;
      const newTaskId = selectedTaskId?.toString() || null;

      if (activeTaskId !== newTaskId) {
        this.timeEntryTaskId.set(activeTaskId);
        this.messageService.add({
          severity: 'warn',
          summary: 'Apontamento em aberto',
          detail:
            'Você tem um apontamento em aberto. Encerre-o para iniciar um novo apontamento em outra tarefa.',
          life: 5000,
        });
        return;
      }
    }

    this.timeEntryTaskId.set(selectedTaskId);
    this.resolveSystem(selectedTaskId);

    if (this._visible && !this._activeTimeEntry) {
      if (selectedTaskId) {
        this.detectAndSetFollowUpType();
      } else {
        this.detectAndSetFollowUpTypeForTicket();
      }
    }
  }

  readonly timeTypes = constants.ticketcenter.ticketLogTypes;
  readonly attendanceTypes = constants.ticketcenter.ticketLogAttendanceTypes;

  taskOptions = computed(() => {
    const tasks = this.ticketTasks();
    if (!tasks || tasks.length === 0) return [];
    return tasks.map(task => ({
      label: task.summary || `Tarefa #${task.id}`,
      value: task.id,
    }));
  });

  ticketStatus = computed(() => {
    return this.ticket?.status?.description || 'Sem status';
  });

  showNewForm(): void {
    this.activeTab.set('new');
  }

  showHistory(): void {
    this.activeTab.set('history');
  }

  private toLocalISOString(date: Date): string {
    const offset = -date.getTimezoneOffset();
    const offsetSign = offset >= 0 ? '+' : '-';
    const offsetHours = Math.floor(Math.abs(offset) / 60)
      .toString()
      .padStart(2, '0');
    const offsetMinutes = (Math.abs(offset) % 60).toString().padStart(2, '0');

    return (
      date.getFullYear() +
      '-' +
      (date.getMonth() + 1).toString().padStart(2, '0') +
      '-' +
      date.getDate().toString().padStart(2, '0') +
      'T' +
      date.getHours().toString().padStart(2, '0') +
      ':' +
      date.getMinutes().toString().padStart(2, '0') +
      ':' +
      date.getSeconds().toString().padStart(2, '0') +
      offsetSign +
      offsetHours +
      ':' +
      offsetMinutes
    );
  }

  /** Concluir só faz sentido ao encerrar, e só para tarefa apontada que ainda esteja aberta. */
  canFinishTask(): boolean {
    if (!this._activeTimeEntry) return false;

    const taskId = this.timeEntryTaskId();
    if (!taskId) return false;

    const task = this.ticketTasks().find(t => t.id.toString() === taskId.toString());
    return !!task && !task.finished;
  }

  stopTimeEntry(): void {
    if (!this.activeTimeEntry) return;

    this.sending.set(true);

    const endTime = this.timeEntryEndDate() || new Date();

    // Verificação segura para obter o ID do apontamento
    if (!this.activeTimeEntry?.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Apontamento não encontrado',
        life: 3000,
      });
      this.sending.set(false);
      return;
    }

    const timeEntryId = this.activeTimeEntry.id;

    // Encerrar o apontamento
    this.ticketService
      .stopTimeEntry(this.ticket.id, {
        time_entry_id: timeEntryId,
        end_date: this.toLocalISOString(endTime),
        movement_description: this.movementDescription().trim(),
        comment_pub: this.commentPub(),
        ticket_status_id: this.timeEntryStatusId() || undefined,
        finish_task: this.canFinishTask() && this.finishTask(),
      })
      .subscribe({
        next: updatedTicket => {
          this.messageService.add({
            severity: 'success',
            summary: 'Apontamento Encerrado',
            detail: 'Apontamento encerrado com sucesso!',
          });
          this.timeEntryAdded.emit(updatedTicket);
          this.timeEntryStopped.emit();
          this.sending.set(false);
          this.onHide();
        },
        // A mensagem do backend vem primeiro: as regras de 422 são específicas ("Apontamento já
        // foi encerrado", "não pertence a este ticket") e um "Tente novamente" genérico esconde
        // justamente as que repetir a ação não resolve.
        error: err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: err?.error?.message || 'Erro ao encerrar apontamento. Tente novamente.',
            life: 5000,
          });
          this.sending.set(false);
        },
      });
  }

  onHide(): void {
    this.visibleChange.emit(false);
    this.resetForm();
    this._activeTimeEntry = null;
  }

  onStartTimeEntry(): void {
    const user = utils.getUser();
    if (!user) return;

    const bwUserCG = utils.getBwUserCompanyGroup(user);
    const correctUserId = bwUserCG?.user_id || user.id;

    if (!this.timeEntryStatusId()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Status é obrigatório',
        life: 3000,
      });
      return;
    }

    this.sending.set(true);

    this.ticketService
      .startTimeEntry(this.ticket.id, {
        movement_description: this.movementDescription().trim(),
        comment_pub: this.commentPub(),
        user_id: correctUserId,
        attendance_type: this.timeEntryAttendanceType(),
        type: this.timeEntryType(),
        department_type: this.timeEntryDepartmentType(),
        start_date: this.toLocalISOString(this.timeEntryStartDate()),
        task_id: this.timeEntryTaskId() || undefined,
        ticket_status_id: this.timeEntryStatusId() || undefined,
        system_id: this.timeEntrySystem()?.id || undefined,
      })
      .subscribe({
        next: updatedTicket => {
          this.sending.set(false);
          this.timeEntryAdded.emit(updatedTicket);
          this.onHide();
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Apontamento iniciado',
            life: 3000,
          });
        },
        // Mesmo motivo do encerramento: "Já existe um apontamento em andamento para este usuário"
        // é acionável, o texto genérico não.
        error: err => {
          this.sending.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: err?.error?.message || 'Erro ao iniciar apontamento',
            life: 5000,
          });
        },
      });
  }

  // `commentPub` fica de fora de propósito: é preferência persistida do usuário, não estado do
  // formulário — resetá-la aqui desfaria a escolha a cada fechamento do modal.
  private resetForm(): void {
    const now = new Date();
    this.movementDescription.set('');
    this.timeEntryType.set('M');
    this.timeEntryAttendanceType.set('I');
    this.timeEntryDepartmentType.set('J');
    this.timeEntryStartDate.set(now);
    this.timeEntryEndDate.set(null);
    this.timeEntryTaskId.set(null);
    this.timeEntryStatusId.set(this.ticket?.status?.id || null);
    this.timeEntrySystem.set(this._ticket?.system ?? null);
    this.finishTask.set(false);
    this.sending.set(false);
  }
}
